import { Form } from 'formik';
import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getCompanyNameFromSsn } from '../../../helpers/formHelpers/formHelpers';
import Button from '../../base/button';
import FormikCheckbox from '../../base/forms/formikCheckbox';
import FormikFieldWrapper from '../../base/forms/formikFieldWrapper';
import { StyledFormikField } from '../../base/forms/input';
import { Column, Columns } from '../../base/grid';
import { ICompanyDebitForm } from '../companyDebitForm.schema';
import * as StyledCompanyDebitForm from '../companyDebitForm.styles';

interface Props {
	values: ICompanyDebitForm;
	setFieldTouched: any;
	setFieldValue: any;
	isValid: any;
	isSubmitting: any;
	isValidating: boolean;
	errors: any;
	touched: any;
}

const CompanyDebitFormForm: FC<Props> = ({ values, errors, setFieldValue, isSubmitting, isValidating }) => {
	const { t } = useTranslation('companyDebitForm');
	const { countLetters, identificationNumber, nameOfCompany } = values;
	const { identificationNumber: identificationNumberError } = errors;
	const lastFetchedIdentificationNumber = useRef('');

	useEffect(() => {
		const trimmedIdentificationNumber = identificationNumber.trimEnd();

		if (identificationNumberError || trimmedIdentificationNumber.length < 9) {
			lastFetchedIdentificationNumber.current = '';

			if (nameOfCompany) {
				setFieldValue('nameOfCompany', '');
			}
			return;
		}

		if (isValidating || lastFetchedIdentificationNumber.current === trimmedIdentificationNumber) {
			return;
		}

		lastFetchedIdentificationNumber.current = trimmedIdentificationNumber;

		getCompanyNameFromSsn(trimmedIdentificationNumber)
			.then(name => {
				if (lastFetchedIdentificationNumber.current === trimmedIdentificationNumber) {
					setFieldValue('nameOfCompany', name);
				}
			})
			.catch(() => {
				if (lastFetchedIdentificationNumber.current === trimmedIdentificationNumber) {
					lastFetchedIdentificationNumber.current = '';
				}
			});
	}, [identificationNumber, identificationNumberError, isValidating, setFieldValue, nameOfCompany]);

	return (
		<Form>
			<Columns>
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						name="identificationNumber"
						label={t('socialId')}
						smallLabel
						languageComponent="companyDebitForm">
						<StyledFormikField
							type="text"
							id="identificationNumber"
							name="identificationNumber"
							placeholder={`${t('socialId')} *`}
						/>
					</FormikFieldWrapper>
				</Column>
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						name="nameOfCompany"
						label={t('nameOfCompany')}
						smallLabel
						languageComponent="companyDebitForm">
						<StyledFormikField type="text" id="nameOfCompany" name="nameOfCompany" disabled />
					</FormikFieldWrapper>
				</Column>
				<Column desktopSmall={1 / 3} tablet={1 / 2}>
					<FormikFieldWrapper name="email" label={t('email')} smallLabel languageComponent="companyDebitForm">
						<StyledFormikField
							type="text"
							id="email"
							name="email"
							placeholder={`${t('email')} *`}
							maxLength="60"
						/>
					</FormikFieldWrapper>
				</Column>
				<Column desktopSmall={1 / 3} tablet={1 / 2}>
					<FormikFieldWrapper name="name" label={t('name')} smallLabel languageComponent="companyDebitForm">
						<StyledFormikField
							type="text"
							id="name"
							name="name"
							placeholder={`${t('name')} *`}
							maxLength="12"
						/>
					</FormikFieldWrapper>
				</Column>
				<Column desktopSmall={1 / 3} tablet={1 / 2}>
					<FormikFieldWrapper
						name="reference"
						label={t('reference')}
						smallLabel
						languageComponent="companyDebitForm">
						<StyledFormikField
							type="text"
							id="reference"
							name="reference"
							placeholder={`${t('reference')}`}
							maxLength="35"
						/>
					</FormikFieldWrapper>
				</Column>
			</Columns>
			<FormikFieldWrapper name="countLetters" languageComponent="companyDebitForm">
				<Columns>
					<Column>
						<FormikCheckbox
							name="countLetters"
							id="countLetters"
							label={t('countLetters')}
							setFieldValue={setFieldValue}
						/>
					</Column>
				</Columns>
			</FormikFieldWrapper>
			{!countLetters && (
				<>
					<StyledCompanyDebitForm.Heading>{t('input')}</StyledCompanyDebitForm.Heading>
					<Columns>
						<Column desktopSmall={1 / 3} tablet={1 / 2}>
							<FormikFieldWrapper
								name="numberOfRegularLetters"
								label={t('numberOfRegularLetters')}
								smallLabel
								languageComponent="companyDebitForm">
								<StyledFormikField
									type="text"
									id="numberOfRegularLetters"
									name="numberOfRegularLetters"
									placeholder={`${t('numberOfRegularLetters')}`}
									maxLength="5"
								/>
							</FormikFieldWrapper>
						</Column>
						<Column desktopSmall={1 / 3} tablet={1 / 2}>
							<FormikFieldWrapper
								name="numberOfEuropeanLetters"
								label={t('numberOfEuropeanLetters')}
								smallLabel
								languageComponent="companyDebitForm">
								<StyledFormikField
									type="text"
									id="numberOfEuropeanLetters"
									name="numberOfEuropeanLetters"
									placeholder={`${t('numberOfEuropeanLetters')}`}
									maxLength="5"
								/>
							</FormikFieldWrapper>
						</Column>
						<Column desktopSmall={1 / 3} tablet={1 / 2}>
							<FormikFieldWrapper
								name="numberOfLettersOutsideEurope"
								label={t('numberOfLettersOutsideEurope')}
								smallLabel
								languageComponent="companyDebitForm">
								<StyledFormikField
									type="text"
									id="numberOfLettersOutsideEurope"
									name="numberOfLettersOutsideEurope"
									placeholder={`${t('numberOfLettersOutsideEurope')}`}
									maxLength="5"
								/>
							</FormikFieldWrapper>
						</Column>
					</Columns>
				</>
			)}
			<Columns>
				<Column>
					<Button mobileBlock wide primary type="submit" style={{ float: 'right' }} isLoading={isSubmitting}>
						{t('register')}
					</Button>
				</Column>
			</Columns>
		</Form>
	);
};

export default CompanyDebitFormForm;
